import { env } from '@/lib/env'

/**
 * Times the control panel couldn't reach the API (no answer, or a gateway error from Cloudflare or the
 * server). The control panel shows its own message for these, so Sentry never hears of them: they wait
 * here and go to the API once it answers again (control panel → Connection errors). Only while
 * the browser thinks it's online, each kind of failure once, and at most 10 per page.
 */
export interface ConnectionReport {
  kind: 'network' | 'gateway'
  status?: number
  method: string
  /** The API path, without its query string. */
  path: string
}

const GATEWAY = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530])
const MAX_PER_PAGE = 10

let waiting: ConnectionReport[] = []
let sent = 0
const seen = new Set<string>()

export const isGatewayStatus = (status: number) => GATEWAY.has(status)

export function noteConnectionFailure(report: ConnectionReport) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  const key = `${report.kind}|${report.status ?? ''}|${report.method}|${report.path}`
  if (seen.has(key) || sent + waiting.length >= MAX_PER_PAGE) return
  seen.add(key)
  waiting.push(report)
}

/** Sends what's waiting; called whenever the API has just answered. */
export function sendConnectionReports() {
  if (waiting.length === 0) return
  const body = JSON.stringify(waiting)
  sent += waiting.length
  waiting = []
  // Plain text and no cookies: a simple request the browser sends without asking the API first.
  void fetch(`${env.apiOrigin}/api/v1/connection-errors/cp`, {
    method: 'POST',
    mode: 'no-cors',
    credentials: 'omit',
    keepalive: true,
    headers: { 'Content-Type': 'text/plain' },
    body,
  }).catch(() => undefined)
}

/** For tests. */
export function resetConnectionReports() {
  waiting = []
  sent = 0
  seen.clear()
}
